import React, {useEffect, useState, useCallback} from 'react'
import {useDeleteFile} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/ResourceList/ResourceUpload'
import {Resource} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/ResourceList/ResourceItem'
import ComponentConfig, {
  ComponentConfigProps,
  RemoveButton,
  SaveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {useForm} from 'react-hook-form'
import {ResourceListProps} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/ResourceList'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import Settings from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/ResourceList/ResourceItemConfig/Settings'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import ScheduleInput from 'organization/Event/DashboardConfig/ComponentConfigPanel/ScheduleInput'
import {generateHashId} from 'lib/crypto/hash'
import {
  useAddTranslation,
  useLanguages,
  useRemoveTranslations,
} from 'Event/LanguageProvider'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {useTemplate} from 'Event/TemplateProvider'
import {get as getAtPath} from 'lodash'
import {useUserTranslations} from 'Event/LanguageProvider/translations'
import {HasServiceTags} from 'organization/Event/Services/service-tags'

export function ResourceItemConfig(
  props: ComponentConfigProps & {
    listId: string
    id?: string
    resource: Resource
    list: ResourceListProps
  },
) {
  const {resource, id, showing, onClose, listId} = props
  const deleteFile = useDeleteFile()
  const {handleSubmit, control, register, setValue, formState} = useForm()
  const translate = useUserTranslations({disableDefaultFallback: true})
  const [rules, setRules] = useState(resource.rules)
  const [isUrl, setIsUrl] = useState(resource.isUrl)
  const [filePath, setFilePath] = useState(translate(resource.filePath))
  const addTranslation = useAddTranslation()
  const removeTranslation = useRemoveTranslations()
  const languages = useLanguages()
  const template = useTemplate()

  const saveTemplate = useSaveTemplate()

  useEffect(() => {
    if (showing) {
      return
    }

    setRules(resource.rules)
    setIsUrl(resource.isUrl)
    setFilePath(translate(resource.filePath))
  }, [resource, showing, translate])

  const update = (id: string, data: Resource) => {
    const name = replaceAtPath(data, 'name', `{{${id}_name}}`)
    const url = replaceAtPath(data, 'url', `{{${id}_url}}`)

    saveTemplate({
      sidebarItems: {
        [listId]: {
          resources: {
            [id]: {
              ...data,
              filePath: `{{${id}_filePath}}`,
            },
          },
        },
      },
      localization: addTranslation({
        [`${id}_name`]: name ?? '',
        [`${id}_url`]: url ?? '',
        [`${id}_filePath`]: filePath ?? '',
      }),
    })
  }

  const insert = (newResource: Resource) => {
    generateHashId([
      'simple_blog_sidebar_resource_item',
      new Date().valueOf().toString(),
      Math.random().toString(),
    ]).then((id) => {
      const componentId = `resource_${id}`
      saveTemplate({
        sidebarItems: {
          [listId]: {
            resources: {
              [componentId]: {
                ...newResource,
                name: `{{${componentId}_name}}`,
                filePath: `{{${componentId}_filePath}}`,
                url: `{{${componentId}_url}}`,
              },
            },
          },
        },
        localization: addTranslation({
          [`${componentId}_name`]: newResource.name ?? '',
          [`${componentId}_filePath`]: newResource.filePath ?? '',
          [`${componentId}_url`]: newResource.url ?? '',
        }),
      })
    })
  }

  const save = (
    form: Pick<Resource, 'name' | 'url' | 'isVisible' | 'icon' | 'actionId'> &
      HasServiceTags,
  ) => {
    const data: Resource = {
      ...form,
      rules,
      isUrl,
      filePath,
    }

    if (id !== undefined) {
      update(id, data)
      onClose()
      return
    }

    insert(data)
    onClose()
  }

  const remove = useCallback(() => {
    if (!id) {
      throw new Error('Called remove outside of editing resource.')
    }

    for (const language of languages) {
      const filePath = getAtPath(
        template,
        `localization.translations.${language.name}.${id}_filePath`,
      )

      if (filePath) {
        deleteFile(filePath).catch((e) => {
          // Log error, but prevent it from crashing
          // app
          console.error(e)
        })
      }
    }

    saveTemplate({
      sidebarItems: {
        [listId]: {
          resources: {
            [id]: REMOVE,
          },
        },
      },
      localization: removeTranslation([
        `${id}_name`,
        `${id}_filePath`,
        `${id}_url`,
      ]),
    })

    onClose()
  }, [
    deleteFile,
    id,
    onClose,
    removeTranslation,
    listId,
    saveTemplate,
    languages,
    template,
  ])

  return (
    <ComponentConfig
      title="Resource"
      onClose={onClose}
      showing={showing}
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <Settings
          control={control}
          register={register}
          resource={resource}
          isUrl={isUrl}
          setIsUrl={setIsUrl}
          filePath={filePath}
          setFilePath={setFilePath}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={setRules}>
        <ScheduleInput
          setValue={setValue}
          control={control}
          values={resource}
        />
      </RulesPanel>
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton
          variant="outlined"
          aria-label="remove resource"
          onClick={remove}
          showing={Boolean(id)}
        >
          REMOVE RESOURCE
        </RemoveButton>
      </Footer>
    </ComponentConfig>
  )
}
